import { localDateInputValue } from '../utils/localDateTime';
// components/IndustryCompetencyBuilder.tsx
import React, { useState, useMemo } from 'react';
import type { CompetencyRecommendationResult, Page, NavParams, EnrolledCourse, Student } from '@types';
import { LMS_LEVELS, MODULE_DATA } from '../../services/lmsData';
import { ICONS } from './constants';

interface IndustryCompetencyBuilderProps {
    industryName: string;
    recommendations: CompetencyRecommendationResult;
    onBack: () => void;
    onCreateCustomCourse: (course: EnrolledCourse, studentId: string) => void;
    students: Student[];
    onCreateStudent: (studentData: { firstName: string, lastName: string, organization: string }) => Student | Promise<Student>;
}

const IndustryCompetencyBuilder: React.FC<IndustryCompetencyBuilderProps> = ({ industryName, recommendations, onBack, onCreateCustomCourse, students, onCreateStudent }) => {
    const [assessmentOption, setAssessmentOption] = useState('online_quiz');
    const [enrollmentCount, setEnrollmentCount] = useState(1);
    const [curriculum, setCurriculum] = useState(recommendations.recommendedModules);
    const [moduleToAdd, setModuleToAdd] = useState('');

    // Student selection state
    const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showNewStudentForm, setShowNewStudentForm] = useState(false);
    const [newStudentData, setNewStudentData] = useState({ firstName: '', lastName: '', organization: '' });


    const allLmsModules = useMemo(() => {
        const modules: { id: string; title: string; level: string }[] = [];
        Object.values(LMS_LEVELS).forEach(levelData => {
            levelData.modules.forEach(mod => {
                if (MODULE_DATA[mod.id]) {
                    modules.push({ id: mod.id, title: mod.title, level: levelData.title });
                }
            });
        });
        return modules;
    }, []);

    const modulesNotInCurriculum = useMemo(() => {
        const currentModuleTitles = new Set(curriculum.map(mod => mod.module));
        return allLmsModules.filter(mod => !currentModuleTitles.has(mod.title));
    }, [curriculum, allLmsModules]);
    
    const availableModules = useMemo(() => {
        const moduleSet = new Set<string>();
        Object.values(LMS_LEVELS).forEach(level => {
            level.modules.forEach(mod => {
                // Check if the module has actual content beyond a placeholder
                if (MODULE_DATA[mod.id] && MODULE_DATA[mod.id].content.introduction !== 'Content coming soon.') {
                    moduleSet.add(mod.title);
                }
            });
        });
        return moduleSet;
    }, []);

    const filteredStudents = useMemo(() => {
        if (!searchTerm.trim()) return [];
        return students.filter(s =>
            `${s.firstName} ${s.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.organization.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, students]);

    const handleRemoveModule = (moduleToRemove: { level: string; module: string; priority: string; }) => {
        setCurriculum(current => current.filter(mod => mod.module !== moduleToRemove.module));
    };

    const handleAddModule = () => {
        if (!moduleToAdd) return;

        const moduleData = allLmsModules.find(mod => mod.title === moduleToAdd);
        if (moduleData) {
            setCurriculum(current => [
                ...current,
                {
                    level: moduleData.level,
                    module: moduleData.title,
                    priority: 'Medium' // Default priority for manually added modules
                }
            ]);
            setModuleToAdd(''); // Reset dropdown selection
        }
    };

    const handleSelectStudent = (student: Student) => {
        setSelectedStudent(student);
        setSearchTerm('');
        setShowNewStudentForm(false);
    };

    const handleCreateNewStudent = async () => {
        if (!newStudentData.firstName || !newStudentData.lastName || !newStudentData.organization) return;
        const newStudent = await Promise.resolve(onCreateStudent(newStudentData));
        handleSelectStudent(newStudent);
        setNewStudentData({ firstName: '', lastName: '', organization: '' });
    };

    const handleCreateCourseAndEnroll = () => {
        if (!selectedStudent) return;
        const newCourse: EnrolledCourse = {
            courseId: `custom-${industryName.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`,
            courseName: `${industryName} Program`,
            enrollmentDate: localDateInputValue(),
            status: 'Not Started',
            progress: 0,
            modules: curriculum.map(m => m.module),
            isCustom: true,
            detailedModules: curriculum,
        };
        onCreateCustomCourse(newCourse, selectedStudent.id);
    };


    const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
        const colors = {
            High: 'bg-red-100 text-red-800',
            Medium: 'bg-yellow-100 text-yellow-800',
            Low: 'bg-green-100 text-green-800',
        };
        return <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${colors[priority as keyof typeof colors] || 'bg-slate-100 text-slate-800'}`}>{priority}</span>;
    };

    return (
        <div className="animate-fade-in max-w-4xl mx-auto">
             <button
                onClick={onBack}
                className="flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 mb-6 transition-colors duration-200"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Dashboard
            </button>

            <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
                <h2 className="text-2xl font-bold text-slate-800">Industry Competency Builder</h2>
                <p className="text-slate-500 mt-1">A tailored course program for the <strong>"{industryName}"</strong> industry.</p>
                
                <div className="mt-6 space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold text-slate-700">Recommended Curriculum</h3>
                        <div className="mt-2 border rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-slate-200">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Level</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Module</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Priority</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-slate-200">
                                    {curriculum.map((mod, index) => {
                                        const isAvailable = availableModules.has(mod.module);
                                        return (
                                            <tr key={index}>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-500">{mod.level}</td>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-slate-900">{mod.module}</td>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm"><PriorityBadge priority={mod.priority} /></td>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm">
                                                    {isAvailable ? (
                                                        <span className="text-green-600 font-semibold">Available</span>
                                                    ) : (
                                                        <span className="text-orange-500">Module under development</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm">
                                                    <button onClick={() => handleRemoveModule(mod)} className="p-1 text-slate-400 hover:text-red-600" aria-label={`Remove ${mod.module}`}>
                                                        {React.cloneElement(ICONS.trash as any, {className: 'h-4 w-4'})}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                     <div>
                        <h3 className="text-lg font-semibold text-slate-700">Add Extra Modules</h3>
                         <div className="mt-2 flex items-center gap-2 p-3 bg-slate-50 border rounded-lg">
                            <select 
                                value={moduleToAdd} 
                                onChange={e => setModuleToAdd(e.target.value)}
                                className="w-full bg-white border border-slate-300 rounded-md p-2 text-sm"
                            >
                                <option value="">Select a module to add...</option>
                                {modulesNotInCurriculum.map(mod => (
                                    <option key={mod.id} value={mod.title}>{mod.title} ({mod.level})</option>
                                ))}
                            </select>
                            <button onClick={handleAddModule} disabled={!moduleToAdd} className="bg-slate-600 hover:bg-slate-700 text-white font-semibold py-2 px-4 rounded-md text-sm disabled:bg-slate-400">
                                Add Module
                            </button>
                        </div>
                    </div>


                    <div className="pt-6 border-t">
                         <h3 className="text-lg font-semibold text-slate-700">Enroll a Student</h3>
                         <p className="text-xs text-slate-500 mt-1 mb-2">Create this program as a custom course and enroll a student.</p>
                         {selectedStudent ? (
                            <div className="mt-2 p-4 bg-green-50 border border-green-200 rounded-lg flex justify-between items-center">
                                <div>
                                    <p className="text-sm text-green-800">Student Selected:</p>
                                    <p className="font-bold text-green-900">{selectedStudent.firstName} {selectedStudent.lastName} ({selectedStudent.organization})</p>
                                </div>
                                <button onClick={() => setSelectedStudent(null)} className="text-sm font-semibold text-slate-600 hover:text-black">Change</button>
                            </div>
                        ) : (
                            <div className="mt-2 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700">Search for Existing Student</label>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        placeholder="Search by name or organization..."
                                        className="mt-1 w-full p-2 border rounded-md"
                                    />
                                    {filteredStudents.length > 0 && (
                                        <div className="mt-2 border rounded-md max-h-40 overflow-y-auto">
                                            {filteredStudents.map(student => (
                                                <button key={student.id} onClick={() => handleSelectStudent(student)} className="w-full text-left p-2 hover:bg-slate-100 border-b">
                                                    {student.firstName} {student.lastName} <span className="text-slate-500">({student.organization})</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-4">
                                    <hr className="flex-grow"/>
                                    <span className="text-sm text-slate-500">OR</span>
                                    <hr className="flex-grow"/>
                                </div>
                                <div>
                                    <button onClick={() => setShowNewStudentForm(!showNewStudentForm)} className="w-full text-sm font-semibold text-blue-600 hover:underline">
                                        {showNewStudentForm ? 'Cancel Enrollment for New Student' : 'Enroll a New Student'}
                                    </button>
                                    {showNewStudentForm && (
                                        <div className="mt-2 p-4 bg-slate-50 border rounded-lg space-y-3">
                                            <input value={newStudentData.firstName} onChange={e => setNewStudentData(p => ({...p, firstName: e.target.value}))} placeholder="First Name" className="w-full p-2 border rounded-md"/>
                                            <input value={newStudentData.lastName} onChange={e => setNewStudentData(p => ({...p, lastName: e.target.value}))} placeholder="Last Name" className="w-full p-2 border rounded-md"/>
                                            <input value={newStudentData.organization} onChange={e => setNewStudentData(p => ({...p, organization: e.target.value}))} placeholder="Organization" className="w-full p-2 border rounded-md"/>
                                            <button onClick={handleCreateNewStudent} className="w-full bg-indigo-600 text-white p-2 rounded-md">Create & Select Student</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={handleCreateCourseAndEnroll}
                                disabled={!selectedStudent}
                                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-md transition-colors flex items-center justify-center disabled:bg-slate-400 disabled:cursor-not-allowed"
                            >
                                {selectedStudent ? `Create Course & Enroll ${selectedStudent.firstName}` : 'Select a Student to Enroll'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IndustryCompetencyBuilder;